import { useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Drawer,
  Checkbox,
  Popover,
  type ButtonSize,
} from '@viquard/doc-ui/components';
import type { CalendarEventStatus, CalendarGroup } from '../types.js';
import { ALL_STATUSES } from '../utils/status.js';
import { uniqueCalendarGroups } from '../utils/identity.js';
import {
  formatMessage,
  resolveStatusLabel,
  useCalendarMessages,
} from '../i18n.js';

export interface MoreFiltersButtonProps {
  readonly groups?: readonly CalendarGroup[] | undefined;
  readonly groupValue: ReadonlySet<string>;
  readonly onGroupChange: (next: Set<string>) => void;
  readonly statusValue: ReadonlySet<CalendarEventStatus>;
  readonly onStatusChange: (next: Set<CalendarEventStatus>) => void;
  /** Render the panel as a bottom Drawer (mobile) instead of a Popover
   *  (desktop). The trigger button itself stays the same. */
  readonly mobile?: boolean | undefined;
  /** Trigger button size. Mobile bumps to `md` for a 44 px touch target. */
  readonly size?: ButtonSize | undefined;
}

/**
 * Combined Group + Status filter — single trigger button for the
 * secondary filters. The active facet count is surfaced via a doc-ui
 * `Badge` so the trigger reads at a glance.
 *
 * Body uses `Checkbox` rows (one section per facet). On mobile the
 * panel is a doc-ui bottom `Drawer`; on desktop it's a `Popover` with
 * the same content.
 */
export function MoreFiltersButton(props: MoreFiltersButtonProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    groupValue,
    onGroupChange,
    statusValue,
    onStatusChange,
    mobile = false,
    size = 'sm',
  } = props;
  const groups =
    props.groups === undefined ? undefined : uniqueCalendarGroups(props.groups);
  const [open, setOpen] = useState(false);

  const showGroups = groups !== undefined && groups.length > 0;
  const activeCount = groupValue.size + statusValue.size;

  const toggleGroup = (id: string): void => {
    const next = new Set(groupValue);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onGroupChange(next);
  };
  const toggleStatus = (s: CalendarEventStatus): void => {
    const next = new Set(statusValue);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    onStatusChange(next);
  };

  const trigger = (
    <Button
      variant={activeCount > 0 ? 'primary' : 'ghost'}
      size={size}
      shape="pill"
      iconRight={
        activeCount > 0 ? (
          <Badge tone="accent" size="sm" variant="solid" numeric>
            {activeCount}
          </Badge>
        ) : undefined
      }
      onClick={mobile ? () => { setOpen(true); } : undefined}
      aria-label={
        activeCount > 0
          ? formatMessage(messages.filtersTriggerActive, { count: activeCount })
          : messages.filtersTrigger
      }
    >
      {messages.filtersTrigger}
    </Button>
  );

  const body = (
    <div className="vq-cal__more-filters">
      {showGroups && (
        <section className="vq-cal__more-filters-section">
          <header className="vq-cal__more-filters-section-head">{messages.filterSectionGroups}</header>
          <div className="vq-cal__more-filters-rows">
            {groups.map((g) => (
              <Checkbox
                key={g.id}
                checked={groupValue.has(g.id)}
                onCheckedChange={() => { toggleGroup(g.id); }}
                label={
                  <span className="vq-cal__more-filters-label">
                    <span
                      className="vq-cal__more-filters-dot"
                      style={
                        g.color !== undefined
                          ? { background: g.color }
                          : undefined
                      }
                      aria-hidden="true"
                    />
                    <span>{g.name}</span>
                  </span>
                }
                size="sm"
              />
            ))}
          </div>
        </section>
      )}

      <section className="vq-cal__more-filters-section">
        <header className="vq-cal__more-filters-section-head">{messages.filterSectionStatus}</header>
        <div className="vq-cal__more-filters-rows">
          {ALL_STATUSES.map((s) => (
            <Checkbox
              key={s}
              checked={statusValue.has(s)}
              onCheckedChange={() => { toggleStatus(s); }}
              label={
                <span className="vq-cal__more-filters-label">
                  <span
                    className="vq-cal__more-filters-dot"
                    data-status={s}
                    aria-hidden="true"
                  />
                  <span>{resolveStatusLabel(messages, s)}</span>
                </span>
              }
              size="sm"
            />
          ))}
        </div>
      </section>
    </div>
  );

  if (mobile) {
    return (
      <>
        {trigger}
        <Drawer
          open={open}
          onOpenChange={setOpen}
          side="bottom"
          size="auto"
          modal
          title={messages.filtersPanelTitle}
        >
          {body}
        </Drawer>
      </>
    );
  }
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      placement="bottom-start"
      content={() => body}
    >
      {trigger}
    </Popover>
  );
}
